"use client";

import type { ImageProps } from "next/image";

import Image from "next/image";
import { useState } from "react";

interface MoviePosterProps extends Omit<
  ImageProps,
  "src" | "fill" | "onError"
> {
  src: string | null | undefined;
  /** Shown when there is no poster, or it fails to load. */
  fallback: React.ReactNode;
}

/**
 * A movie poster filling its positioned container. A missing poster, or one
 * that fails to load (e.g. TMDB replaced it after the page was cached), shows
 * `fallback` instead of a broken image. Next's Image re-fires `onError` after
 * hydration, so this also covers posters that failed before the page was
 * interactive.
 */
export function MoviePoster({
  src,
  fallback,
  alt,
  ...props
}: MoviePosterProps) {
  // Remember which src failed, so a different poster gets its own chance.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (!src || src === failedSrc) return fallback;

  return (
    <Image
      fill
      alt={alt}
      src={src}
      onError={() => setFailedSrc(src)}
      {...props}
    />
  );
}
